import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import NotificationsAlertsCustomTemplateEditorComponent from 'webapp/components/notifications/alerts/custom-template-editor';

import Alert from 'webapp/models-custom/alert';

interface Args {
	model: Alert;
}

export default class NotificationsAlertsCustomTemplateComponent extends Component<Args> {
	@tracked editor: AceEditor | null = null; // instance of custom editor, set through child component
	@tracked editorTemplate: string; // text (coming from custom editor side)
	@tracked updateTemplate = false;

	alertMainKeywords = ['datasource', 'metric', 'period', 'condition', 'qualifier', 'value'];

	_defaultTemplate: string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.editorTemplate = this.convertToEditorFormat(
			this.args.model.template || this.args.model.defaultTemplate,
		);
		this._defaultTemplate = this.args.model.defaultTemplate;
	}

	get shownKeywords() {
		const shownKeywords: { keyword: string; style: SafeString }[] = [];
		this.alertMainKeywords.forEach((keyword) => {
			const index = +keyword.slice(-1) || 0;
			shownKeywords.push({
				keyword: keyword,
				style: htmlSafe(
					'color:' +
						Alert.colors[index]!.text +
						';background-color:' +
						Alert.colors[index]!.bg,
				),
			});
		});
		return shownKeywords;
	}

	convertToEditorFormat(text: string) {
		let editorTemplate = text || '';

		// replace ::keyword:: with [keyword]
		this.alertMainKeywords.forEach((keyword) => {
			editorTemplate = editorTemplate.split('::' + keyword + '::').join('[' + keyword + ']');
		});

		return editorTemplate;
	}

	convertToAPIFormat(editorTemplate: string) {
		let text = editorTemplate || '';

		// replace [keyword] with ::keyword::
		this.alertMainKeywords.forEach((keyword) => {
			text = text.split('[' + keyword + ']').join('::' + keyword + '::');
		});

		return text;
	}

	@action
	updateAlertTemplate(text: string) {
		this.editorTemplate = text;
		const template = this.convertToAPIFormat(text);

		if (this.args.model.template !== template) {
			this.args.model.template = template;
		}
	}

	@action
	updateTemplateFromDefault() {
		const previousDefault = this._defaultTemplate,
			defaultTemplate = this.args.model.defaultTemplate;

		let newTemplate = '';

		if (!this.args.model.template) {
			newTemplate = defaultTemplate;
		} else if (this.args.model.template.includes(previousDefault)) {
			newTemplate = this.args.model.template.replace(previousDefault, defaultTemplate);
		}

		if (newTemplate && newTemplate !== this.args.model.template) {
			this.args.model.template = newTemplate;

			const editorTemplate = this.convertToEditorFormat(newTemplate);
			if (this.editorTemplate !== editorTemplate) {
				this.editorTemplate = editorTemplate;
				this.updateTemplate = true;
			}
		}

		this._defaultTemplate = defaultTemplate;
	}

	@action
	setEditor(editor: AceEditor) {
		this.editor = editor; // need it for addKeyword
	}

	@action
	addKeyword(keyword: string) {
		const editor = this.editor;
		if (editor) {
			editor.insert('[' + keyword + ']');
			editor.focus();
		}
	}

	@action
	resetUpdateTemplate() {
		this.updateTemplate = false;
	}
	<template>
		<div class="template" {{didUpdate this.updateTemplateFromDefault @model.condition}}>
			<NotificationsAlertsCustomTemplateEditorComponent
				@text={{this.editorTemplate}}
				@updateEditor={{this.updateTemplate}}
				@resetUpdateEditor={{this.resetUpdateTemplate}}
				@onEditorInit={{this.setEditor}}
				@onTextChange={{this.updateAlertTemplate}}
			/>
		</div>

		<div class="keywords">
			<span>Available strings:</span>
			<ul>
				{{#each this.shownKeywords as |item|}}
					<li
						role="button"
						{{on "click" (fn this.addKeyword item.keyword)}}
						style={{item.style}}
						class="keyword-{{item.keyword}}"
					>
						{{item.keyword}}
					</li>
				{{/each}}
			</ul>
		</div>
	</template>
}
